import { loadScript } from '../../../utils';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps';
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_MAPS_SCRIPT_SRC = 'https://maps.googleapis.com/maps/api/js';
const GSI_SCRIPT_ID = 'gsi';

const DEFAULT_TIMEOUT = 20000;
const DEFAULT_ATTEMPTS = 2;

let mapsApi;
let autocompleteService;
let placesService;
let googleIdentity;

let initPromise;

const waitFor = (check, timeout = DEFAULT_TIMEOUT) =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const tick = () => {
      if (check()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt > timeout) {
        reject(new Error('Timeout waiting for Google API to become ready'));
        return;
      }

      setTimeout(tick, 50);
    };

    tick();
  });

const loadWithRetry = async (src, id, attempts = DEFAULT_ATTEMPTS) => {
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      await loadScript(src, id);
      return;
    } catch (error) {
      lastError = error;

      if (attempt < attempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
      }
    }
  }

  throw lastError;
};

const getMapsScriptSrc = () =>
  `${GOOGLE_MAPS_SCRIPT_SRC}?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places&callback=Function.prototype`;

const initMaps = async () => {
  if (mapsApi) {
    return;
  }

  if (!window.google?.maps) {
    await loadWithRetry(getMapsScriptSrc(), GOOGLE_MAPS_SCRIPT_ID);
  }

  await waitFor(() => window.google?.maps?.places?.PlacesService);

  mapsApi = window.google.maps;
  autocompleteService = new window.google.maps.places.AutocompleteService();
  placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
};

const initIdentity = async () => {
  if (googleIdentity) {
    return;
  }

  if (!window.google?.accounts?.id) {
    await loadWithRetry(GSI_SCRIPT_SRC, GSI_SCRIPT_ID);
    await waitFor(() => window.google?.accounts?.id);
  }

  googleIdentity = window.google.accounts.id;
};

export const init = async () => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      await initMaps();
      await initIdentity();
    } catch (error) {
      initPromise = null;
      console.error('Error initializing Google services:', error);
      throw error;
    }
  })();

  return initPromise;
};

export const getApis = () => ({
  mapsApi,
  autocompleteService,
  placesService,
  googleIdentity,
});